import pako from 'pako'
import * as base64 from 'base64-js'

export function compressString(str: string) {
  try {
    const compressed = pako.deflate(str)
    return base64.fromByteArray(compressed)
  } catch (e) {
    console.warn(e)
  }
  return undefined
}

export function decompressString(str: string) {
  try {
    const decompressed = pako.inflate(base64.toByteArray(str), { to: 'string' })
    return decompressed
  } catch (e) {
    console.warn(e)
  }
  return undefined
}

// NEW: Type-safe compression wrapper
export interface CompressedData<T = unknown> {
  _compressed: true
  data: string
  _type?: T // For TypeScript type inference only
}

// NEW: Type for data that might be compressed
export type MaybeCompressed<T> = T | CompressedData<T>

// NEW: Check if data is compressed
export function isCompressed<T>(data: MaybeCompressed<T>): data is CompressedData<T> {
  return (
    data !== null &&
    data !== undefined &&
    typeof data === 'object' &&
    '_compressed' in data &&
    data._compressed === true &&
    'data' in data &&
    typeof data.data === 'string'
  )
}

// NEW: Compress any serializable data
export function compressData<T>(data: T): CompressedData<T> | T {
  if (data === null || data === undefined) {
    return data
  }

  try {
    const jsonString = JSON.stringify(data)
    const compressed = compressString(jsonString)

    if (compressed) {
      return {
        _compressed: true,
        data: compressed
      }
    }
  } catch (e) {
    console.warn('Failed to compress data:', e)
  }

  // Return original data if compression fails
  return data
}

// NEW: Decompress data
export function decompressData<T>(data: MaybeCompressed<T>): T {
  if (!isCompressed(data)) {
    return data
  }

  try {
    const decompressed = decompressString(data.data)
    if (decompressed) {
      return JSON.parse(decompressed)
    }
  } catch (e) {
    console.warn('Failed to decompress data:', e)
  }

  // This should never happen, but TypeScript needs it
  throw new Error('Failed to decompress data')
}

// NEW: Ensure data is compressed (for migration)
export function ensureCompressed<T>(data: MaybeCompressed<T>): CompressedData<T> | T {
  if (isCompressed(data)) {
    return data
  }
  return compressData(data)
}
